/**
 * MapToolsContext - Contexto para herramientas del mapa
 *
 * @version 1.1
 * @updated 2026-02-16
 * @module core/map-tools
 *
 * @description
 * Contexto global para manejar herramientas activas del mapa y elementos creados.
 *
 * @remarks
 * **Estado**:
 * - activeTool: Herramienta actualmente activa
 * - trees: Array de árboles creados (puntos)
 * - selectedTreeId: ID del árbol seleccionado
 *
 * **Herramientas**:
 * - create-tree: Crea árboles (puntos) en el mapa
 */

import { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { Marker } from 'maplibre-gl';
import { mapEngine } from '../map-engine/MapEngineService';
import type { TreeComplete, Coordinates } from './tree-types';
import { treesApi, treeToCreateDto } from './trees-api';
import type { TreeResponse } from './trees-api';
import type { Fountain } from './fountain-types';
import { fountainsApi, fountainToCreateDto } from './fountains-api';
import type { FountainResponse } from './fountains-api';
import { useProject } from '../projects';

let activeToolSnapshot: string | null = null;

export function getActiveMapToolSnapshot(): string | null {
  return activeToolSnapshot;
}

/**
 * Convierte una respuesta del backend (TreeResponse) a un objeto Tree frontend.
 * Los campos opcionales se leen del JSONB `data`.
 */
function treeFromResponse(t: TreeResponse): Tree {
  return {
    id: t.id,
    code: t.code,
    coordinates: t.coordinates,
    createdAt: new Date(t.created_at),
    ...(t.data ?? {}),
  } as Tree;
}

/**
 * Convierte una respuesta del backend (FountainResponse) a un objeto Fountain frontend.
 * Los campos opcionales se leen del JSONB `data`.
 */
function fountainFromResponse(f: FountainResponse): Fountain {
  return {
    id: f.id,
    code: f.code,
    coordinates: f.coordinates,
    type: f.type,
    status: f.status,
    createdAt: new Date(f.created_at),
    updatedAt: new Date(f.updated_at),
    ...(f.data ?? {}),
  } as Fountain;
}

/**
 * Genera un código temporal para árbol mientras no se guarda.
 *
 * @remarks
 * Este código es temporal y será reemplazado por el sistema de codificación
 * geoespacial del backend cuando se guarde el árbol.
 * El backend generará un código con formato: TR_CELDA_SEQ_FECHA
 * (e.g. "TR_AA001_005_25022026")
 */
function generateTemporaryTreeCode(): string {
  return `TEMP-TREE-${Date.now()}`;
}

/**
 * Genera un código temporal para contenedor mientras no se guarda.
 *
 * @remarks
 * Este código es temporal y será reemplazado por el sistema de codificación
 * geoespacial del backend cuando se guarde el contenedor.
 */
function generateTemporaryContainerCode(): string {
  return `TEMP-CONT-${Date.now()}`;
}

/**
 * Genera un código temporal para fuente mientras no se guarda.
 *
 * @remarks
 * Este código es temporal y será reemplazado por el sistema de codificación
 * geoespacial del backend cuando se guarde la fuente.
 * El backend generará un código con formato: FT_CELDA_SEQ_FECHA
 */
function generateTemporaryFountainCode(): string {
  return `TEMP-FOUNTAIN-${Date.now()}`;
}

/**
 * Tipo Container para residuos
 */
export interface Container {
  id: string;
  code: string;
  coordinates: Coordinates;
  containerType: 'general' | 'recyclable' | 'organic' | 'glass' | 'hazardous';
  capacity: number; // litros
  currentFill?: number; // 0-100%
  status: 'active' | 'full' | 'damaged' | 'removed';
  address?: string;
  zoneId?: string;
  createdAt: Date;
  updatedAt?: Date;
  metadata?: Record<string, unknown>;
  marker?: Marker;
}

/**
 * Tipo Tree exportado para compatibilidad
 */
export type Tree = TreeComplete;

/**
 * Estado del contexto
 */
export interface MapToolsState {
  activeTool: string | null;
  trees: Tree[];
  selectedTreeId: string | null;
  fountains: Fountain[];
  selectedFountainId: string | null;
  containers: Container[];
  selectedContainerId: string | null;
  /**
   * Selecciones genéricas para todos los módulos
   * @remarks
   * Formato: { 'mobility-stops': 'stop-id-123', 'urban-parcels': 'parcel-456', ... }
   * Permite rastrear el elemento activo en el sidebar para cualquier módulo sin modificar la interfaz.
   */
  selectedElements: Record<string, string | null>;
}

/**
 * Acciones del contexto
 */
export interface MapToolsActions {
  setActiveTool: (tool: string | null) => void;
  addTree: (coordinates: Coordinates, autoSelect?: boolean) => Tree;
  updateTree: (updatedTree: Tree, originalId?: string) => void;
  selectTree: (treeId: string | null) => void;
  removeTree: (treeId: string) => void;
  saveTree: (tree: Tree, projectId: string) => Promise<Tree>;
  addFountain: (coordinates: Coordinates, autoSelect?: boolean) => Fountain;
  updateFountain: (updatedFountain: Fountain, originalId?: string) => void;
  selectFountain: (fountainId: string | null) => void;
  removeFountain: (fountainId: string) => void;
  saveFountain: (fountain: Fountain, projectId: string) => Promise<Fountain>;
  addContainer: (coordinates: Coordinates) => Container;
  updateContainer: (updatedContainer: Container) => void;
  selectContainer: (containerId: string | null) => void;
  removeContainer: (containerId: string) => void;
  /**
   * Selecciona un elemento genérico de cualquier módulo
   * @param key - Identificador del módulo-tab (ej: 'mobility-stops', 'urban-parcels')
   * @param elementId - ID del elemento seleccionado o null para deseleccionar
   */
  selectElement: (key: string, elementId: string | null) => void;
  /**
   * Obtiene el ID del elemento seleccionado para un módulo-tab
   * @param key - Identificador del módulo-tab
   * @returns ID del elemento seleccionado o null
   */
  getSelectedElement: (key: string) => string | null;
}

/**
 * Tipo completo del contexto
 */
export type MapToolsContextType = MapToolsState & MapToolsActions;

/**
 * Contexto
 */
const MapToolsContext = createContext<MapToolsContextType | undefined>(undefined);

/**
 * Props del provider
 */
export interface MapToolsProviderProps {
  children: ReactNode;
}

/**
 * Nivel de zoom mínimo para mostrar árboles.
 * Zoom 10 = nivel de barrio ampliado; por debajo solo se ven regiones.
 * El fitBounds estándar de proyecto (~10km² de área) resulta en zoom ~13,
 * así que los markers deben ser visibles a partir de ese nivel.
 */
const MIN_ZOOM_FOR_TREES = 10;

/**
 * Nivel de zoom mínimo para mostrar fuentes.
 * Mismo umbral que árboles: visibles desde la vista de ciudad completa.
 */
const MIN_ZOOM_FOR_FOUNTAINS = 10;

/**
 * Nivel de zoom mínimo para mostrar contenedores.
 * Mismo umbral que árboles: visibles desde la vista de ciudad completa.
 */
const MIN_ZOOM_FOR_CONTAINERS = 10;

/**
 * Provider del contexto
 */
export function MapToolsProvider({ children }: MapToolsProviderProps): JSX.Element {
  const { projectId } = useProject();
  const [activeTool, setActiveTool] = useState<string | null>(null);
  const [trees, setTrees] = useState<Tree[]>([]);
  const [selectedTreeId, setSelectedTreeId] = useState<string | null>(null);
  const [fountains, setFountains] = useState<Fountain[]>([]);
  const [selectedFountainId, setSelectedFountainId] = useState<string | null>(null);
  const [containers, setContainers] = useState<Container[]>([]);
  const [selectedContainerId, setSelectedContainerId] = useState<string | null>(null);
  const [selectedElements, setSelectedElements] = useState<Record<string, string | null>>({});
  /** Ref para cancelar operaciones async si el componente desmonta o cambia el proyecto. */
  const cancelRef = useRef(false);

  useEffect(() => {
    activeToolSnapshot = activeTool;
    return () => {
      activeToolSnapshot = null;
    };
  }, [activeTool]);

  /**
   * Crea el elemento DOM para el marker de árbol
   */
  const createMarkerElement = (isSelected: boolean): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = `tree-marker ${isSelected ? 'selected' : ''}`;
    return el;
  };

  /**
   * Crea el elemento DOM para el marker de fuente
   */
  const createFountainMarkerElement = (isSelected: boolean): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = `fountain-marker ${isSelected ? 'selected' : ''}`;
    return el;
  };

  /**
   * Crea el elemento DOM para el marker de contenedor
   */
  const createContainerMarkerElement = (isSelected: boolean): HTMLDivElement => {
    const el = document.createElement('div');
    el.className = `container-marker ${isSelected ? 'selected' : ''}`;
    return el;
  };

  /**
   * Carga los árboles existentes del proyecto desde la API y crea sus marcadores.
   * Reintenta hasta que el mapa esté disponible (máx. ~5 s).
   *
   * ✨ DESACTIVADO: Carga automática removida para que solo Red Urbana aparezca al inicio.
   * Los árboles se cargarán cuando el usuario active el módulo Environmental explícitamente.
   */
  useEffect(() => {
    cancelRef.current = false;

    if (!projectId) {
      // Sin proyecto: limpiar todos los marcadores y el estado
      setTrees(prev => {
        prev.forEach(t => t.marker?.remove());
        return [];
      });
      setSelectedTreeId(null);
      return;
    }

    // ✨ CORRECCIÓN: Carga automática desactivada
    // Los árboles ya no se cargan automáticamente al abrir el proyecto.
    // Solo se verá la Red Urbana al inicio (estado base del proyecto).
    // Para cargar árboles, el usuario debe activar el módulo Environmental.

    // TODO: Implementar carga explícita cuando se active el módulo Environmental
    // Mover esta lógica a un método público loadTrees() que se llame desde EnvironmentalPanel

    /* CÓDIGO DESACTIVADO - Carga automática original:

    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    const load = async (): Promise<void> => {
      try {
        const data = await treesApi.getByProject(projectId);
        if (cancelRef.current || data.length === 0) return;

        const tryCreateMarkers = (attempts = 0): void => {
          if (cancelRef.current) return;
          const map = mapEngine.getMap();
          if (!map) {
            if (attempts < 25) {
              retryTimer = setTimeout(() => tryCreateMarkers(attempts + 1), 200);
            }
            return;
          }

          const currentZoom = map.getZoom();
          const shouldShow = currentZoom >= MIN_ZOOM_FOR_TREES;

          const loaded: Tree[] = data.map(resp => {
            const tree = treeFromResponse(resp);
            const markerEl = createMarkerElement(false);

            if (!shouldShow) {
              markerEl.style.display = 'none';
            }

            const marker = new Marker({ element: markerEl })
              .setLngLat(resp.coordinates)
              .addTo(map);
            markerEl.addEventListener('click', () => {
              setSelectedTreeId(tree.id);
            });
            tree.marker = marker;
            return tree;
          });

          if (!cancelRef.current) setTrees(loaded);
        };

        tryCreateMarkers();
      } catch (err) {
        console.error('[MapTools] Error al cargar árboles existentes:', err);
      }
    };

    void load();

    return () => {
      cancelRef.current = true;
      if (retryTimer !== null) clearTimeout(retryTimer);
    };

    */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  /**
   * Controla la visibilidad de los árboles según el nivel de zoom
   * Solo muestra árboles cuando zoom >= MIN_ZOOM_FOR_TREES
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const updateTreesVisibility = (): void => {
      const zoom = map.getZoom();
      const shouldShow = zoom >= MIN_ZOOM_FOR_TREES;

      trees.forEach(tree => {
        const el = tree.marker?.getElement();
        if (el) {
          el.style.display = shouldShow ? 'block' : 'none';
        }
      });
    };

    // Actualizar visibilidad inicial
    updateTreesVisibility();

    // Escuchar cambios de zoom
    map.on('zoom', updateTreesVisibility);

    return () => {
      map.off('zoom', updateTreesVisibility);
    };
  }, [trees]);

  /**
   * Controla la visibilidad de las fuentes según el nivel de zoom
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const updateFountainsVisibility = (): void => {
      const zoom = map.getZoom();
      const shouldShow = zoom >= MIN_ZOOM_FOR_FOUNTAINS;

      fountains.forEach(fountain => {
        const el = fountain.marker?.getElement();
        if (el) {
          el.style.display = shouldShow ? 'block' : 'none';
        }
      });
    };

    // Actualizar visibilidad inicial
    updateFountainsVisibility();

    // Escuchar cambios de zoom
    map.on('zoom', updateFountainsVisibility);

    return () => {
      map.off('zoom', updateFountainsVisibility);
    };
  }, [fountains]);

  /**
   * Controla la visibilidad de los contenedores según el nivel de zoom
   */
  useEffect(() => {
    const map = mapEngine.getMap();
    if (!map) return;

    const updateContainersVisibility = (): void => {
      const zoom = map.getZoom();
      const shouldShow = zoom >= MIN_ZOOM_FOR_CONTAINERS;

      containers.forEach(container => {
        const el = container.marker?.getElement();
        if (el) {
          el.style.display = shouldShow ? 'block' : 'none';
        }
      });
    };

    // Actualizar visibilidad inicial
    updateContainersVisibility();

    // Escuchar cambios de zoom
    map.on('zoom', updateContainersVisibility);

    return () => {
      map.off('zoom', updateContainersVisibility);
    };
  }, [containers]);

  /**
   * Agrega un nuevo árbol con valores por defecto
   *
   * @param coordinates - Coordenadas donde crear el árbol
   * @param autoSelect - Si true, selecciona automáticamente el árbol creado (default: true)
   *
   * @remarks
   * El código temporal será reemplazado por un código geoespacial
   * cuando se guarde el árbol en el backend.
   *
   * Cuando autoSelect = false, el árbol se crea en el mapa pero no se selecciona
   * ni se abre el panel. El usuario debe hacer clic en el marker para seleccionarlo.
   */
  const addTree = (coordinates: Coordinates, autoSelect: boolean = true): Tree => {
    const now = new Date();
    const treeId = `tree-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newTree: Tree = {
      // Sistema
      id: treeId,
      coordinates,
      createdAt: now,

      // PESTAÑA 1: GENERAL
      code: generateTemporaryTreeCode(), // Código temporal, será reemplazado al guardar

      // Datos administrativos mínimos
      registryStatus: 'active',
      registrationDate: now,
      registeredBy: 'Sistema', // TODO: Obtener usuario actual
      lastUpdate: now,
      updatedBy: 'Sistema', // TODO: Obtener usuario actual
    };

    // Intentar agregar marker al mapa si está disponible
    const map = mapEngine.getMap();
    if (map) {
      const markerEl = createMarkerElement(autoSelect); // Aplicar estilo selected solo si autoSelect

      // Aplicar visibilidad inicial basada en zoom
      const currentZoom = map.getZoom();
      const shouldShow = currentZoom >= MIN_ZOOM_FOR_TREES;

      if (!shouldShow) {
        markerEl.style.display = 'none';
      }

      const marker = new Marker({ element: markerEl })
        .setLngLat(coordinates)
        .addTo(map);

      // Agregar click listener al marker
      markerEl.addEventListener('click', () => {
        setSelectedTreeId(newTree.id);
        console.log('[MapTools] Árbol clickeado:', newTree.id);
      });

      newTree.marker = marker;
    }

    setTrees((prev) => [...prev, newTree]);

    // Solo seleccionar automáticamente si autoSelect = true
    if (autoSelect) {
      setSelectedTreeId(newTree.id);
      console.log('[MapTools] Árbol creado y seleccionado:', newTree);
    } else {
      console.log('[MapTools] Árbol creado (sin seleccionar):', newTree);
    }

    return newTree;
  };

  /**
   * Actualiza un árbol existente
   *
   * @param updatedTree - Árbol con datos actualizados
   * @param originalId - ID original del árbol (opcional). Se usa cuando el ID cambió
   *                     de temporal a ID de BD tras guardarse.
   *
   * @remarks
   * Si se proporciona originalId, busca el árbol por ese ID y lo reemplaza con updatedTree.
   * Esto es necesario porque al guardar en BD, el ID temporal se reemplaza con el ID real.
   */
  const updateTree = (updatedTree: Tree, originalId?: string): void => {
    setTrees((prev) => prev.map((tree) => {
      const idToMatch = originalId ?? updatedTree.id;
      return tree.id === idToMatch ? updatedTree : tree;
    }));
    console.log('[MapTools] Árbol actualizado:', updatedTree.id, originalId ? `(ID anterior: ${originalId})` : '');
  };

  /**
   * Selecciona un árbol
   */
  const selectTree = (treeId: string | null): void => {
    console.log('[MapTools] → selectTree llamado con:', treeId);
    console.log('[MapTools]   selectedTreeId actual:', selectedTreeId);

    if (selectedTreeId === treeId) {
      console.log('[MapTools] ⚠ El árbol ya está seleccionado, ignorando...');
      return;
    }

    setSelectedTreeId(treeId);
    console.log('[MapTools] ✓ Árbol seleccionado:', treeId);
  };

  /**
   * Elimina un árbol
   */
  const removeTree = (treeId: string): void => {
    setTrees((prev) => {
      const tree = prev.find((t) => t.id === treeId);
      if (tree?.marker) {
        tree.marker.remove(); // Eliminar marker del mapa
      }
      return prev.filter((t) => t.id !== treeId);
    });
    if (selectedTreeId === treeId) {
      setSelectedTreeId(null);
    }
    console.log('[MapTools] Árbol eliminado:', treeId);
  };

  /**
   * Guarda un árbol en la base de datos
   *
   * @returns El árbol guardado con el ID de BD (si era nuevo) o el mismo árbol (si ya existía)
   *
   * @remarks
   * - Árboles NUEVOS (ID temporal 'tree-...'): Crea en BD y actualiza con ID real
   * - Árboles EXISTENTES: Los actualiza en BD (cuando se implemente el endpoint UPDATE)
   * - Actualiza selectedTreeId si el árbol seleccionado cambió de ID
   */
  const saveTree = async (tree: Tree, projectId: string): Promise<Tree> => {
    try {
      const isNewTree = tree.id.startsWith('tree-');
      console.log('[MapTools] Guardando árbol:', tree.id, isNewTree ? '(NUEVO)' : '(EXISTENTE)');

      // Convertir el árbol a DTO
      const dto = treeToCreateDto(tree, projectId);

      // Llamar a la API
      const savedTree = await treesApi.create(dto);

      console.log('[MapTools] Árbol guardado exitosamente:', savedTree);

      // Si era un árbol nuevo, actualizar con el ID del backend
      if (isNewTree) {
        const originalId = tree.id;
        const updatedTree = {
          ...tree,
          id: savedTree.id,
          code: savedTree.code,
        };

        // Actualizar el marker para que apunte al árbol con el nuevo ID
        const treeInArray = trees.find(t => t.id === originalId);
        if (treeInArray?.marker) {
          updatedTree.marker = treeInArray.marker;
        }

        // Actualizar en el array de árboles (pasando el ID original)
        updateTree(updatedTree, originalId);

        // Actualizar selectedTreeId si este árbol estaba seleccionado
        setSelectedTreeId((prevId) => {
          if (prevId === originalId) {
            console.log('[MapTools] Actualizando selectedTreeId:', originalId, '→', savedTree.id);
            return savedTree.id;
          }
          return prevId;
        });

        return updatedTree;
      }

      // Árbol existente: solo retornar (TODO: implementar UPDATE cuando exista el endpoint)
      return tree;

    } catch (error) {
      console.error('[MapTools] Error al guardar árbol:', error);
      throw error;
    }
  };

  /**
   * Agrega una nueva fuente con valores por defecto
   *
   * @param coordinates - Coordenadas donde crear la fuente
   * @param autoSelect - Si true, selecciona automáticamente la fuente creada (default: true)
   *
   * @remarks
   * El código temporal será reemplazado por un código geoespacial
   * cuando se guarde la fuente en el backend.
   */
  const addFountain = (coordinates: Coordinates, autoSelect: boolean = true): Fountain => {
    const now = new Date();
    const fountainId = `fountain-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newFountain: Fountain = {
      // Sistema
      id: fountainId,
      coordinates,
      createdAt: now,
      updatedAt: now,
      code: generateTemporaryFountainCode(), // Código temporal, será reemplazado al guardar

      // General - campos obligatorios
      type: 'drinking', // Tipo por defecto: bebedero

      // Operational
      status: 'active',
      waterQuality: undefined,
      lastQualityTest: undefined,
      flowRate: undefined,
      hasFilter: false,
      filterType: undefined,
      lastFilterChange: undefined,
      hasDrainage: false,
      drainageCondition: undefined,
      surroundingCondition: undefined,
      hasLighting: false,

      // Maintenance
      needsIntervention: false,

      // Accessibility
      hasWheelchairAccess: false,
      hasPetBowl: false,
      childFriendly: false,
      hasHistoricalValue: false,
      isProtected: false,
    };

    // Intentar agregar marker al mapa si está disponible
    const map = mapEngine.getMap();
    if (map) {
      const markerEl = createFountainMarkerElement(autoSelect);

      // Aplicar visibilidad inicial basada en zoom
      const currentZoom = map.getZoom();
      const shouldShow = currentZoom >= MIN_ZOOM_FOR_FOUNTAINS;

      if (!shouldShow) {
        markerEl.style.display = 'none';
      }

      const marker = new Marker({ element: markerEl })
        .setLngLat(coordinates)
        .addTo(map);

      // Agregar click listener al marker
      markerEl.addEventListener('click', () => {
        setSelectedFountainId(newFountain.id);
        console.log('[MapTools] Fuente clickeada:', newFountain.id);
      });

      newFountain.marker = marker;
    }

    setFountains((prev) => [...prev, newFountain]);

    // Solo seleccionar automáticamente si autoSelect = true
    if (autoSelect) {
      setSelectedFountainId(newFountain.id);
      console.log('[MapTools] Fuente creada y seleccionada:', newFountain);
    } else {
      console.log('[MapTools] Fuente creada (sin seleccionar):', newFountain);
    }

    return newFountain;
  };

  /**
   * Actualiza una fuente existente
   *
   * @param updatedFountain - Fuente con datos actualizados
   * @param originalId - ID original de la fuente (opcional). Se usa cuando el ID cambió
   *                     de temporal a ID de BD tras guardarse.
   */
  const updateFountain = (updatedFountain: Fountain, originalId?: string): void => {
    setFountains((prev) => prev.map((fountain) => {
      const idToMatch = originalId ?? updatedFountain.id;
      return fountain.id === idToMatch ? updatedFountain : fountain;
    }));
    console.log('[MapTools] Fuente actualizada:', updatedFountain.id, originalId ? `(ID anterior: ${originalId})` : '');
  };

  /**
   * Selecciona una fuente
   */
  const selectFountain = (fountainId: string | null): void => {
    console.log('[MapTools] → selectFountain llamado con:', fountainId);
    console.log('[MapTools]   selectedFountainId actual:', selectedFountainId);

    if (selectedFountainId === fountainId) {
      console.log('[MapTools] ⚠ La fuente ya está seleccionada, ignorando...');
      return;
    }

    setSelectedFountainId(fountainId);
    console.log('[MapTools] ✓ Fuente seleccionada:', fountainId);
  };

  /**
   * Elimina una fuente
   */
  const removeFountain = (fountainId: string): void => {
    setFountains((prev) => {
      const fountain = prev.find((f) => f.id === fountainId);
      if (fountain?.marker) {
        fountain.marker.remove(); // Eliminar marker del mapa
      }
      return prev.filter((f) => f.id !== fountainId);
    });
    if (selectedFountainId === fountainId) {
      setSelectedFountainId(null);
    }
    console.log('[MapTools] Fuente eliminada:', fountainId);
  };

  /**
   * Guarda una fuente en la base de datos
   *
   * @returns La fuente guardada con el ID de BD (si era nueva) o la misma fuente (si ya existía)
   *
   * @remarks
   * - Fuentes NUEVAS (ID temporal 'fountain-...'): Crea en BD y actualiza con ID real
   * - Fuentes EXISTENTES: Las actualiza en BD (cuando se implemente el endpoint UPDATE)
   * - Actualiza selectedFountainId si la fuente seleccionada cambió de ID
   */
  const saveFountain = async (fountain: Fountain, projectId: string): Promise<Fountain> => {
    try {
      const isNewFountain = fountain.id.startsWith('fountain-');
      console.log('[MapTools] Guardando fuente:', fountain.id, isNewFountain ? '(NUEVA)' : '(EXISTENTE)');

      // Convertir la fuente a DTO
      const dto = fountainToCreateDto(fountain, projectId);

      // Llamar a la API
      const savedFountain = await fountainsApi.create(dto);

      console.log('[MapTools] Fuente guardada exitosamente:', savedFountain);

      // Si era una fuente nueva, actualizar con el ID del backend
      if (isNewFountain) {
        const originalId = fountain.id;
        const updatedFountain = {
          ...fountain,
          id: savedFountain.id,
          code: savedFountain.code,
        };

        // Actualizar el marker para que apunte a la fuente con el nuevo ID
        const fountainInArray = fountains.find(f => f.id === originalId);
        if (fountainInArray?.marker) {
          updatedFountain.marker = fountainInArray.marker;
        }

        // Actualizar en el array de fuentes (pasando el ID original)
        updateFountain(updatedFountain, originalId);

        // Actualizar selectedFountainId si esta fuente estaba seleccionada
        setSelectedFountainId((prevId) => {
          if (prevId === originalId) {
            console.log('[MapTools] Actualizando selectedFountainId:', originalId, '→', savedFountain.id);
            return savedFountain.id;
          }
          return prevId;
        });

        return updatedFountain;
      }

      // Fuente existente: solo retornar (TODO: implementar UPDATE cuando exista el endpoint)
      return fountain;

    } catch (error) {
      console.error('[MapTools] Error al guardar fuente:', error);
      throw error;
    }
  };

  /**
   * Agrega un nuevo contenedor con valores por defecto
   *
   * @remarks
   * El código temporal será reemplazado por un código geoespacial
   * cuando se guarde el contenedor en el backend.
   */
  const addContainer = (coordinates: Coordinates): Container => {
    const now = new Date();
    const containerId = `container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    const newContainer: Container = {
      id: containerId,
      code: generateTemporaryContainerCode(), // Código temporal, será reemplazado al guardar
      coordinates,
      containerType: 'general',
      capacity: 1000, // 1000 litros por defecto
      currentFill: 0,
      status: 'active',
      createdAt: now,
    };

    // Intentar agregar marker al mapa si está disponible
    const map = mapEngine.getMap();
    if (map) {
      const markerEl = createContainerMarkerElement(true); // Nuevo contenedor siempre empieza seleccionado

      // Aplicar visibilidad inicial basada en zoom
      const currentZoom = map.getZoom();
      const shouldShow = currentZoom >= MIN_ZOOM_FOR_CONTAINERS;

      if (!shouldShow) {
        markerEl.style.display = 'none';
      }

      const marker = new Marker({ element: markerEl })
        .setLngLat(coordinates)
        .addTo(map);

      // Agregar click listener al marker
      markerEl.addEventListener('click', () => {
        setSelectedContainerId(newContainer.id);
        console.log('[MapTools] Contenedor clickeado:', newContainer.id);
      });

      newContainer.marker = marker;
    }

    setContainers((prev) => [...prev, newContainer]);
    setSelectedContainerId(newContainer.id);

    console.log('[MapTools] Contenedor creado:', newContainer);

    return newContainer;
  };

  /**
   * Actualiza un contenedor existente
   */
  const updateContainer = (updatedContainer: Container): void => {
    setContainers((prev) => prev.map((c) => (c.id === updatedContainer.id ? updatedContainer : c)));
    console.log('[MapTools] Contenedor actualizado:', updatedContainer.id);
  };

  /**
   * Selecciona un contenedor
   */
  const selectContainer = (containerId: string | null): void => {
    setSelectedContainerId(containerId);
    console.log('[MapTools] Contenedor seleccionado:', containerId);
  };

  /**
   * Elimina un contenedor
   */
  const removeContainer = (containerId: string): void => {
    setContainers((prev) => {
      const container = prev.find((c) => c.id === containerId);
      if (container?.marker) {
        container.marker.remove(); // Eliminar marker del mapa
      }
      return prev.filter((c) => c.id !== containerId);
    });
    if (selectedContainerId === containerId) {
      setSelectedContainerId(null);
    }
    console.log('[MapTools] Contenedor eliminado:', containerId);
  };

  /**
   * Selecciona un elemento genérico de cualquier módulo
   */
  const selectElement = (key: string, elementId: string | null): void => {
    console.log(`[MapTools] → selectElement('${key}', '${elementId}')`);
    setSelectedElements((prev) => ({
      ...prev,
      [key]: elementId,
    }));
  };

  /**
   * Obtiene el ID del elemento seleccionado para un módulo-tab
   */
  const getSelectedElement = (key: string): string | null => {
    return selectedElements[key] ?? null;
  };

  /**
   * Actualiza la clase CSS de los markers de árboles cuando cambia la selección
   */
  useEffect(() => {
    trees.forEach((tree) => {
      if (tree.marker) {
        const markerEl = tree.marker.getElement();
        if (tree.id === selectedTreeId) {
          markerEl.classList.add('selected');
        } else {
          markerEl.classList.remove('selected');
        }
      }
    });
  }, [selectedTreeId, trees]);

  /**
   * Actualiza la clase CSS de los markers de fuentes cuando cambia la selección
   */
  useEffect(() => {
    fountains.forEach((fountain) => {
      if (fountain.marker) {
        const markerEl = fountain.marker.getElement();
        if (fountain.id === selectedFountainId) {
          markerEl.classList.add('selected');
        } else {
          markerEl.classList.remove('selected');
        }
      }
    });
  }, [selectedFountainId, fountains]);

  /**
   * Actualiza la clase CSS de los markers de contenedores cuando cambia la selección
   */
  useEffect(() => {
    containers.forEach((container) => {
      if (container.marker) {
        const markerEl = container.marker.getElement();
        if (container.id === selectedContainerId) {
          markerEl.classList.add('selected');
        } else {
          markerEl.classList.remove('selected');
        }
      }
    });
  }, [selectedContainerId, containers]);

  /**
   * Escucha el evento tree-zoom despachado por TreesPanel.
   * Vuela al centro de las coordenadas indicadas.
   */
  useEffect(() => {
    const handleZoom = (e: Event): void => {
      const { coordinates } = (e as CustomEvent<{ coordinates: [number, number] }>).detail;
      mapEngine.getMap()?.flyTo({ center: coordinates, zoom: 17, duration: 700 });
    };
    window.addEventListener('tree-zoom', handleZoom);
    return () => window.removeEventListener('tree-zoom', handleZoom);
  }, []);

  /**
   * Escucha el evento fountain-zoom despachado por FountainsPanel.
   * Vuela al centro de las coordenadas indicadas.
   */
  useEffect(() => {
    const handleZoom = (e: Event): void => {
      const { coordinates } = (e as CustomEvent<{ coordinates: [number, number] }>).detail;
      mapEngine.getMap()?.flyTo({ center: coordinates, zoom: 17, duration: 700 });
    };
    window.addEventListener('fountain-zoom', handleZoom);
    return () => window.removeEventListener('fountain-zoom', handleZoom);
  }, []);

  /**
   * Escucha el evento container-zoom despachado por ContainersPanel.
   * Vuela al centro de las coordenadas indicadas.
   */
  useEffect(() => {
    const handleZoom = (e: Event): void => {
      const { coordinates } = (e as CustomEvent<{ coordinates: [number, number] }>).detail;
      mapEngine.getMap()?.flyTo({ center: coordinates, zoom: 17, duration: 700 });
    };
    window.addEventListener('container-zoom', handleZoom);
    return () => window.removeEventListener('container-zoom', handleZoom);
  }, []);

  /**
   * Escucha el evento building-zoom despachado por BuildingInventoryList.
   * Vuela al centroide del edificio seleccionado desde el panel izquierdo.
   */
  useEffect(() => {
    const handleZoom = (e: Event): void => {
      const { coordinates } = (e as CustomEvent<{ coordinates: [number, number] }>).detail;
      mapEngine.getMap()?.flyTo({ center: coordinates, zoom: 17, duration: 700 });
    };
    window.addEventListener('building-zoom', handleZoom);
    return () => window.removeEventListener('building-zoom', handleZoom);
  }, []);

  /**
   * Cleanup: eliminar todos los markers cuando se desmonta el componente
   */
  useEffect(() => {
    return () => {
      // Usar una referencia al estado actual para cleanup
      setTrees((currentTrees) => {
        currentTrees.forEach((tree) => {
          if (tree.marker) {
            tree.marker.remove();
          }
        });
        return currentTrees;
      });
      setFountains((currentFountains) => {
        currentFountains.forEach((fountain) => {
          if (fountain.marker) {
            fountain.marker.remove();
          }
        });
        return currentFountains;
      });
      setContainers((currentContainers) => {
        currentContainers.forEach((container) => {
          if (container.marker) {
            container.marker.remove();
          }
        });
        return currentContainers;
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Solo ejecutar en unmount

  const value: MapToolsContextType = {
    // Estado
    activeTool,
    trees,
    selectedTreeId,
    fountains,
    selectedFountainId,
    containers,
    selectedContainerId,
    selectedElements,
    // Acciones
    setActiveTool,
    addTree,
    updateTree,
    selectTree,
    removeTree,
    saveTree,
    addFountain,
    updateFountain,
    selectFountain,
    removeFountain,
    saveFountain,
    addContainer,
    updateContainer,
    selectContainer,
    removeContainer,
    selectElement,
    getSelectedElement,
  };

  return <MapToolsContext.Provider value={value}>{children}</MapToolsContext.Provider>;
}

/**
 * Hook para usar el contexto
 */
export function useMapTools(): MapToolsContextType {
  const context = useContext(MapToolsContext);
  if (!context) {
    throw new Error('useMapTools must be used within MapToolsProvider');
  }
  return context;
}
